import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useGetBillingInfo, useOrganization } from "@/queries/organization-queries";
import {
    useCreditConsumptionTimeSeries,
    filterMetricsByKind,
    type PlainCreditUsageDataPoint,
} from "@/queries/billing-queries";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { MetricConsumptionTimeSeries_Kind } from "gitpod-next-api/gitpod/v1/billing_pb";
import { type FC, useRef, useState } from "react";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { Code, ConnectError } from "@connectrpc/connect";
import { ConsumptionChart } from "@/routes/settings/organization/credit-usage/ConsumptionChart";
import { CreditSummaryCard } from "@/routes/settings/organization/credit-usage/CreditSummaryCard";
import { ForecastChart } from "@/routes/settings/organization/credit-usage/ForecastChart";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";

function formatDate(dp: PlainCreditUsageDataPoint): string {
    if (!dp.time) return "";
    const d = new Date(Number(dp.time.seconds) * 1000);
    // Use UTC so the label matches the calendar day the backend aggregated,
    // regardless of the viewer's local timezone.
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function sumOCU(series: PlainCreditUsageDataPoint[]): number {
    return series.reduce((acc, dp) => acc + dp.ocu, 0);
}

type DaysRange = "7" | "30";

const rangeLabels: Record<DaysRange, string> = {
    "7": "7 days",
    "30": "30 days",
};

const CreditUsagePage: FC = () => {
    useDocumentTitle("Usage");

    const [days, setDays] = useState<DaysRange>("7");
    const [showForecast, setShowForecast] = useState(false);
    const navigate = useNavigate();

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    const organizationId = organization?.id ?? "";
    const tier = organization?.tier;

    const {
        data: consumptionData,
        isLoading: isLoadingConsumption,
        error,
    } = useCreditConsumptionTimeSeries(organizationId, Number(days));

    const { data: billingInfo } = useGetBillingInfo();

    const bannerStateRef = useRef({
        isOutOfCredits: false,
        daysUntilEmpty: undefined as number | undefined,
        isRunningLow: false,
    });

    if (isLoadingMembership || isLoadingOrganization) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (tier === OrganizationTier.FREE || tier === OrganizationTier.ENTERPRISE) {
        return <NotAuthorized />;
    }

    if (error instanceof ConnectError && (error.code === Code.NotFound || error.code === Code.FailedPrecondition)) {
        return (
            <Banner
                variant="info"
                data-testid="credit-usage-not-enabled"
                className="mt-6 max-w-[550px]"
                text={
                    <div className="pl-2">
                        <Text>Usage data is not available for your organization&apos;s current plan.</Text>
                        <Text>To view credit consumption, please contact your account representative.</Text>
                    </div>
                }
            />
        );
    }

    if (error) {
        return (
            <Banner
                variant="danger"
                data-testid="usage-error"
                className="mt-6 max-w-[550px]"
                text={
                    <div className="pl-2">
                        <Text>Failed to load usage data. Please try again later.</Text>
                    </div>
                }
            />
        );
    }

    const metrics = consumptionData?.metrics;
    const totalMetric = filterMetricsByKind(metrics, MetricConsumptionTimeSeries_Kind.ALL);

    const hasData = totalMetric && totalMetric.series.length > 0 && totalMetric.series.some((dp) => dp.ocu > 0);

    const chartData = hasData
        ? totalMetric.series.map((dp) => ({
              date: formatDate(dp),
              total: dp.ocu,
          }))
        : [];

    const totalOCU = hasData ? sumOCU(totalMetric.series) : 0;

    const availableCredits = billingInfo?.availableCredits;

    const avgDailyBurn = chartData.length > 0 ? totalOCU / chartData.length : 0;
    const canForecast = availableCredits !== undefined && availableCredits > 0 && avgDailyBurn >= 1;

    const forecastButton = (
        <Button
            variant="outline"
            size="sm"
            disabled={!canForecast}
            onClick={() => setShowForecast((prev) => !prev)}
            data-tracking-id="toggle-forecast"
        >
            {showForecast ? "Back to Usage" : "Forecast"}
        </Button>
    );

    const topUpAction = {
        text: "Add Credits",
        onClick: () => navigate("/settings/billing"),
        "data-tracking-id": "add-credits-usage-page",
        variant: "primary" as const,
    };

    const nextIsOutOfCredits = availableCredits !== undefined && availableCredits <= 0;
    const nextDaysUntilEmpty =
        avgDailyBurn > 0 && availableCredits ? Math.ceil(availableCredits / avgDailyBurn) : undefined;
    const nextIsRunningLow = !nextIsOutOfCredits && nextDaysUntilEmpty !== undefined && nextDaysUntilEmpty < 7;

    // Keep banner state stable while consumption data is refetching (e.g. switching 7d ↔ 30d).
    if (!isLoadingConsumption) {
        bannerStateRef.current = {
            isOutOfCredits: nextIsOutOfCredits,
            daysUntilEmpty: nextDaysUntilEmpty,
            isRunningLow: nextIsRunningLow,
        };
    }
    const { isOutOfCredits, daysUntilEmpty, isRunningLow } = bannerStateRef.current;

    return (
        <div className="flex flex-col gap-6" data-testid="credit-usage-page">
            <div className="flex items-center gap-1 text-sm text-content-strong">
                <span>View your credit consumption breakdown over the last</span>
                <Select value={days} onValueChange={(v) => setDays(v as DaysRange)}>
                    <SelectTrigger
                        loading={false}
                        className="inline-flex w-auto gap-1 rounded-md border-border-light px-2 py-1 text-sm"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">
                            <span>7 days</span>
                        </SelectItem>
                        <SelectItem value="30">
                            <span>30 days</span>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {isOutOfCredits && (
                <Banner
                    variant="warning"
                    data-testid="out-of-credits-banner"
                    text="You've used all your credits. Add more to keep your environments and agents running smoothly."
                    action={topUpAction}
                />
            )}

            {isRunningLow && (
                <Banner
                    variant="info"
                    data-testid="low-credits-banner"
                    text={
                        <span>
                            <span>
                                {`You have about ${daysUntilEmpty} ${daysUntilEmpty === 1 ? "day" : "days"} of credits left at this pace. `}
                            </span>
                            <button
                                type="button"
                                className="underline hover:no-underline"
                                onClick={() => setShowForecast(true)}
                                data-tracking-id="low-credits-show-forecast"
                            >
                                View forecast
                            </button>
                        </span>
                    }
                    action={topUpAction}
                />
            )}

            <SkeletonBlock ready={!!billingInfo} className="h-[106px] w-full rounded-xl bg-surface-muted">
                {billingInfo && (
                    <CreditSummaryCard
                        totalCredits={billingInfo.totalCredits}
                        availableCredits={billingInfo.availableCredits}
                        usedCredits={billingInfo.usedCredits}
                    />
                )}
            </SkeletonBlock>

            {showForecast && canForecast ? (
                <ForecastChart
                    availableCredits={availableCredits}
                    avgDailyBurn={avgDailyBurn}
                    headerAction={forecastButton}
                    loading={isLoadingConsumption}
                />
            ) : (
                <ConsumptionChart
                    title="Total Usage"
                    dataKey="total"
                    data={chartData}
                    color="#0048FF"
                    totalOCU={hasData ? totalOCU : undefined}
                    daysLabel={rangeLabels[days]}
                    headerAction={hasData ? forecastButton : undefined}
                    loading={isLoadingConsumption}
                    empty={!isLoadingConsumption && !hasData}
                />
            )}
        </div>
    );
};

export default CreditUsagePage;
